// d:\Code\debate-app\src\SessionPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
// Import necessary Firebase functions, including remove
import { ref, onValue, update, set, onDisconnect, remove, serverTimestamp } from 'firebase/database';
import { db } from './firebase'; // Assuming firebase.js exports db and functions correctly
import {
    Accordion, AccordionSummary, AccordionDetails,
    Typography, Box, Button, TextField, CircularProgress, Paper, Container, Stack, Divider, Alert, Chip, List, ListItem, ListItemText, // Added List, ListItem, ListItemText
    Dialog, DialogActions, DialogContent, DialogTitle, IconButton, DialogContentText, ListItemIcon  // Keep Dialog imports for Config Dialog
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// Icons
import PersonIcon from '@mui/icons-material/Person';
import GavelIcon from '@mui/icons-material/Gavel';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle'; // Online+Taken
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'; // Available
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'; // Offline+Taken
import SettingsIcon from '@mui/icons-material/Settings';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
// --- REMOVED: LogoutIcon no longer needed for leave button ---
// import LogoutIcon from '@mui/icons-material/Logout';
import WarningAmberIcon from '@mui/icons-material/WarningAmber'; // Icon for Config Dialog Warning (if needed)
import DownloadIcon from '@mui/icons-material/Download'; // Icon for Download
// Icons for "How to Use" section
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import FormatListNumberedOutlinedIcon from '@mui/icons-material/FormatListNumberedOutlined';
import AvTimerOutlinedIcon from '@mui/icons-material/AvTimerOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import SpeakerNotesOutlinedIcon from '@mui/icons-material/SpeakerNotesOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
// Custom Components
import CountdownTimer from './components/CountdownTimer';
import PrepTimer from './components/PrepTimer';
import SegmentTimerWithProgress from './components/SegmentTimerWithProgress';
// PDF Library
import jsPDF from 'jspdf'; // Import jsPDF
import AdSenseUnit from './components/AdSenseUnit'; // Import AdSenseUnit


// --- Define segments outside component ---
const ldSegments = [
    { name: 'Affirmative Constructive', duration: 360000, allowedRoles: ['affirmative'] }, // 6 min
    { name: 'Cross-Examination', duration: 180000, allowedRoles: ['negative'] }, // 3 min
    { name: 'Negative Constructive', duration: 420000, allowedRoles: ['negative'] }, // 7 min
    { name: 'Cross-Examination', duration: 180000, allowedRoles: ['affirmative'] }, // 3 min
    { name: 'First Affirmative Rebuttal', duration: 240000, allowedRoles: ['affirmative'] }, // 4 min
    { name: 'Negative Rebuttal', duration: 360000, allowedRoles: ['negative'] }, // 6 min
    { name: 'Second Affirmative Rebuttal', duration: 180000, allowedRoles: ['affirmative'] }, // 3 min
];

const pfSegments = [
    { name: 'Team A Speaker 1 (Pro)', duration: 240000, allowedRoles: ['firstPro'] }, // 4 min
    { name: 'Team B Speaker 1 (Con)', duration: 240000, allowedRoles: ['firstCon'] }, // 4 min
    { name: 'Crossfire', duration: 180000, allowedRoles: ['firstPro', 'firstCon'] }, // 3 min
    { name: 'Team A Speaker 2 (Pro)', duration: 240000, allowedRoles: ['secondPro'] }, // 4 min
    { name: 'Team B Speaker 2 (Con)', duration: 240000, allowedRoles: ['secondCon'] }, // 4 min
    { name: 'Crossfire', duration: 180000, allowedRoles: ['secondPro', 'secondCon'] }, // 3 min
    { name: 'Team A Summary (Pro)', duration: 180000, allowedRoles: ['firstPro', 'secondPro'] }, // 3 min (Either speaker)
    { name: 'Team B Summary (Con)', duration: 180000, allowedRoles: ['firstCon', 'secondCon'] }, // 3 min (Either speaker)
    { name: 'Grand Crossfire', duration: 180000, allowedRoles: ['firstPro', 'secondPro', 'firstCon', 'secondCon'] }, // 3 min (All speakers)
    { name: 'Team A Final Focus (Pro)', duration: 120000, allowedRoles: ['firstPro', 'secondPro'] }, // 2 min (Either speaker)
    { name: 'Team B Final Focus (Con)', duration: 120000, allowedRoles: ['firstCon', 'secondCon'] }, // 2 min (Either speaker)
];

// --- Helper function to format role names ---
const formatRoleName = (roleKey) => {
    if (!roleKey) return '';
    return roleKey
        .replace(/([A-Z])/g, ' $1') // Add space before caps
        .replace(/^./, (str) => str.toUpperCase()); // Capitalize first letter
};

// --- Helper function to generate or get user ID ---
const getUserId = () => {
    let userId = localStorage.getItem('debateUserId');
    if (!userId) {
        userId = Math.random().toString(36).substr(2, 9); // Generate new ID
        localStorage.setItem('debateUserId', userId); // Store it
    }
    return userId;
};

// --- Define Role Descriptions ---
const roleDescriptions = {
  LD: {
    affirmative: "Argues in favor of the resolution. Presents constructive arguments and rebuttals.",
    negative: "Argues against the resolution. Presents constructive arguments and rebuttals.",
    judge: "Observes the round, takes notes, and ultimately decides the winner."
  },
  PF: {
    firstPro: "First speaker for the Pro team. Delivers the Pro constructive speech and participates in the first crossfire.",
    secondPro: "Second speaker for the Pro team. Delivers the Pro rebuttal, summary, and final focus. Participates in the second crossfire and grand crossfire.",
    firstCon: "First speaker for the Con team. Delivers the Con constructive speech and participates in the first crossfire.",
    secondCon: "Second speaker for the Con team. Delivers the Con rebuttal, summary, and final focus. Participates in the second crossfire and grand crossfire.",
    judge: "Observes the round, takes notes, and ultimately decides the winner."
  }
};


export default function SessionPage() {
  const theme = useTheme();
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(undefined);
  const [role, setRole] = useState('');
  const [userId] = useState(getUserId);
  const [loadingRole, setLoadingRole] = useState(false);
  const [roleBeingJoined, setRoleBeingJoined] = useState(null); 
  const [errorState, setErrorState] = useState('');
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [newPrepTimeMinutes, setNewPrepTimeMinutes] = useState(4);
  const [configError, setConfigError] = useState('');
  const [rejoinCheckComplete, setRejoinCheckComplete] = useState(false);
  const [serverTimeOffset, setServerTimeOffset] = useState(0);
  const [currentTime, setCurrentTime] = useState(Date.now());
  // State for judge notes responsiveness
  const [localNotes, setLocalNotes] = useState({});

  // --- Effect for fetching session data, auto-rejoin, and termination check ---
  useEffect(() => {
    // Reset state on session ID change
    setSession(undefined);
    setErrorState('');
    setRejoinCheckComplete(false);
    setRole(''); // Also reset role on session change
    setLocalNotes({}); // Reset local notes

    console.log(`[Session Listener] Setting up for session: ${sessionId}. User ID: ${userId}`);
    const sessionRef = ref(db, `sessions/${sessionId}`);

    const unsubscribe = onValue(sessionRef, snap => {
      console.log(`[Session Listener] Data received for ${sessionId}. Snapshot exists: ${snap.exists()}`);
      if (snap.exists()) {
        const sessionData = snap.val();
        setSession(sessionData); // Update UI state

        // --- Auto-rejoin logic (runs only once per mount/sessionId change) ---
        setRejoinCheckComplete(currentRejoinState => {
            // Only attempt rejoin check if it hasn't been completed yet for this mount
            if (!currentRejoinState) {
                console.log(`[Session Listener] Performing rejoin check (rejoinCheckComplete=${currentRejoinState}).`);
                const participantData = sessionData.participants?.[userId];
                if (participantData?.role) {
                    console.log(`[Session Listener] Rejoin successful: Found user ${userId} with role ${participantData.role}. Setting role state.`);
                    setRole(participantData.role); // Set the role state
                } else {
                     console.log(`[Session Listener] Rejoin check: User ${userId} not found in participants or has no role.`);
                }
                return true; // Mark rejoin check as completed for this mount
            }
            // If already completed, just return true without modifying role
            // console.log(`[Session Listener] Rejoin check already completed (rejoinCheckComplete=${currentRejoinState}).`);
            return currentRejoinState;
        });
        // --- End Auto-rejoin logic ---

        // Update config state if needed (safe even if sessionData.totalPrepTime is null/undefined)
        if (sessionData?.totalPrepTime && !isConfigOpen) {
            setNewPrepTimeMinutes(Math.floor(sessionData.totalPrepTime / 60000));
        }
        // Clear any previous error if data loads successfully
        if (errorState) {
            console.log("[Session Listener] Clearing previous error state.");
            setErrorState('');
        }

        // --- REMOVED: Client-Side Session Termination Check ---
        // This check was causing sessions to be deleted on refresh due to race conditions.
        // Reliable termination when the last user leaves abruptly requires server-side functions.
        /*
        const participants = sessionData.participants;
        if (participants && Object.keys(participants).length > 0) {
            const participantIds = Object.keys(participants);
            const allOffline = participantIds.every(pId =>
                !participants[pId] || participants[pId]?.online === false
            );
            if (allOffline) {
                console.warn(`[Termination Check] All participants detected as offline for session ${sessionId}. Attempting removal.`);
                remove(sessionRef)
                    .then(() => console.log(`[Termination Check] Session ${sessionId} removed by client.`))
                    .catch(err => console.error(`[Termination Check] Client failed to remove session ${sessionId}:`, err));
            }
        }
        */
        // --- End REMOVED Termination Check ---

      } else {
        // Session doesn't exist or was deleted
        console.warn(`[Session Listener] Session data for ${sessionId} not found or was deleted.`);
        setSession(null); // Explicitly set to null
        if (!errorState) { // Avoid overwriting specific Firebase read errors
             setErrorState(`Session with ID "${sessionId}" not found. It may have ended or been deleted.`);
        }
        setRejoinCheckComplete(true); // Mark check complete even if session not found
      }

    }, (error) => {
        // Handle Firebase read errors
        console.error("[Session Listener] Firebase read failed:", error);
        setErrorState(`Error loading session: ${error.message}.`);
        setSession(null); // Ensure session is null on error
        setRejoinCheckComplete(true); // Mark check complete on error
    });

    // Cleanup function for the session listener
    return () => {
        console.log("[Session Listener] Unsubscribing:", sessionId);
        unsubscribe();
    };
  }, [sessionId, userId]); // Rerun ONLY if sessionId or userId changes

  // --- Effect for fetching server time offset ---
  useEffect(() => {
      const offsetRef = ref(db, '.info/serverTimeOffset');
      const unsubscribe = onValue(offsetRef, (snap) => {
          setServerTimeOffset(snap.val() ?? 0);
      }, (error) => {
          console.error("Error fetching server time offset:", error);
          setServerTimeOffset(0);
      });
      return () => unsubscribe();
  }, []);

  // --- Effect for updating currentTime to force re-renders (for timers) ---
  useEffect(() => {
      const timer = setInterval(() => setCurrentTime(Date.now()), 500);
      return () => clearInterval(timer);
  }, []);

  // --- Effect for Firebase Presence Management ---
  // Sets online: true on connect and queues onDisconnect operations
  useEffect(() => {
    // Only run if we have essential info: session loaded, userId, and role selected
    // NOTE: We check session *value* here, but don't depend on it below to avoid re-runs
    if (session === undefined || session === null || !userId || !role) {
        // console.log("[Presence Effect] Skipping: Missing session, userId, or role."); // Optional log
        return;
    }

    console.log(`[Presence Effect] Running for user ${userId}, role ${role}, session ${sessionId}`);

    const participantRef = ref(db, `sessions/${sessionId}/participants/${userId}`);
    // Ensure serverTimestamp is available (imported at top or from ./firebase)

    // Define the data to write on disconnect
    const onDisconnectData = {
        online: false,
        lastSeen: serverTimestamp() // Use Firebase server timestamp
    };
    // Define the data to write on connect
    const onConnectData = {
        online: true
    };

    const connectedRef = ref(db, '.info/connected');
    const notesRef = ref(db, `sessions/${sessionId}/notes`);

    // Get handles for onDisconnect operations (queued on Firebase server)
    const userStatusDisconnectOp = onDisconnect(participantRef);
    const notesDisconnectOp = onDisconnect(notesRef);

    // Listener for Firebase connection status
    const unsubscribeConnected = onValue(connectedRef, (snap) => {
        const isConnected = snap.val() === true;
        console.log(`[Presence Effect] Firebase connection status for ${userId}: ${isConnected}`);

        if (isConnected) {
            // --- Actions when connected ---
            console.log(`[Presence Effect] Setting user ${userId} online and role ${role}.`);
            update(participantRef, { ...onConnectData, role: role }) // Ensure role is also set/updated
                .catch(err => console.error("[Presence Effect] Error setting user online:", err));

            console.log(`[Presence Effect] Queuing onDisconnect update for user ${userId}.`);
            userStatusDisconnectOp.update(onDisconnectData)
                .catch(err => console.error("[Presence Effect] Error setting onDisconnect for status:", err));

            if (role === 'judge') {
                console.log(`[Presence Effect] Queuing onDisconnect remove() for notes (judge ${userId}).`);
                notesDisconnectOp.remove()
                    .catch(err => console.error("[Presence Effect] Error setting onDisconnect for notes:", err));
            } else {
                // Ensure any previously queued notes removal is cancelled if role changed
                notesDisconnectOp.cancel()
                    .catch(err => console.warn("[Presence Effect] Error cancelling notes onDisconnect for non-judge:", err));
            }
        }
    });

    // --- Cleanup function for the Presence Effect ---
    return () => {
        console.log(`[Presence Effect Cleanup] Cleaning up for user ${userId}, role ${role}, session ${sessionId}.`);
        unsubscribeConnected(); // Stop listening to connection changes
        // Cancel the queued onDisconnect operations if unmounting cleanly
        userStatusDisconnectOp.cancel()
            .catch(err => console.warn("[Presence Effect Cleanup] Error cancelling user status update onDisconnect:", err));
        if (role === 'judge') {
            notesDisconnectOp.cancel()
                .catch(err => console.warn("[Presence Effect Cleanup] Error cancelling notes onDisconnect:", err));
        }
    };
    // --- MODIFIED: Removed 'session' from dependencies ---
    // Rerun this effect only if session ID, user ID, or role changes
  }, [sessionId, userId, role]); // Dependencies are correct

  // --- Effect to warn judge before closing tab/browser ---
  useEffect(() => {
    if (role === 'judge') {
      const handleBeforeUnload = (event) => {
        const confirmationMessage = 'Are you sure you want to leave? Your notes will be permanently deleted.';
        console.log("[BeforeUnload] Judge attempting to close. Triggering prompt.");
        event.preventDefault();
        event.returnValue = confirmationMessage;
        return confirmationMessage;
      };
      console.log("[BeforeUnload] Adding listener for judge.");
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => {
        console.log("[BeforeUnload] Removing listener for judge.");
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [role]);

  // --- Effect to sync localNotes when session.notes changes from Firebase ---
  useEffect(() => {
    // Only run if session is loaded and not null
    if (session && session !== null) {
        if (session.notes) {
            // Basic check to avoid loops if the update originated from this client
            if (JSON.stringify(session.notes) !== JSON.stringify(localNotes)) {
                 console.log("[Notes Sync] Updating localNotes from session.notes");
                 setLocalNotes(session.notes);
            }
        } else if (Object.keys(localNotes).length > 0) {
            // Reset local notes if session.notes is missing or null/undefined
            console.log("[Notes Sync] Resetting localNotes as session.notes is empty/missing.");
            setLocalNotes({});
        }
    } else if (Object.keys(localNotes).length > 0) {
        // Reset if session becomes null/undefined and localNotes isn't empty
         console.log("[Notes Sync] Resetting localNotes as session is null/undefined.");
         setLocalNotes({});
    }
    // Depend on session object; changes to session.notes will trigger this
  }, [session]);


  // --- Function to handle joining a role ---
  const joinRole = async (selectedRole) => {
    if (session === undefined || session === null) { alert("Session data is not loaded yet."); return; }
    if (session.roles?.[selectedRole]) { alert(`${formatRoleName(selectedRole)} role is already taken.`); return; }
    setLoadingRole(true);
    setRoleBeingJoined(selectedRole);
    try {
        const updates = {
            [`roles/${selectedRole}`]: userId,
            // --- MODIFIED: Ensure only role and online: true are set on join ---
            [`participants/${userId}`]: { role: selectedRole, online: true }
        };
        if (selectedRole === 'judge') {
            // Initialize notes as an empty object if it doesn't exist or is null
            if (!session.notes) {
                updates['notes'] = {};
                console.log("New judge joined, initializing notes object.");
            }
        }
        await update(ref(db, `sessions/${sessionId}`), updates);
        // Set role locally AFTER successful update. Presence effect will handle online status.
        setRole(selectedRole);
    } catch (error) {
        console.error("Failed to join role:", error);
        alert(`Failed to join role: ${error.message}`);
    } finally {
        setLoadingRole(false);
        setRoleBeingJoined(null);
    }
  };

  // --- Handler Functions ---
  const handleStartTimer = (duration) => {
    if (!session) {
        console.error("[handleStartTimer] Error: session data not available.");
        return;
    }
    const isPrepARunning = session.prepTimers?.sideA?.running ?? false;
    const isPrepBRunning = session.prepTimers?.sideB?.running ?? false;
    const anyPrepRunning = isPrepARunning || isPrepBRunning;

    if (anyPrepRunning) {
        console.warn("[handleStartTimer] Cannot start segment: Prep timer running.");
        alert("Pause prep timer to start segment");
        return;
    }
    // Check if timer is *already* running for this segment
    if (session.segmentEndTime && (Date.now() + serverTimeOffset < session.segmentEndTime)) {
        console.warn("[handleStartTimer] Segment timer already running.");
        return;
    }

    const estimatedServerNow = Date.now() + serverTimeOffset;
    const endTimestamp = estimatedServerNow + duration;
    console.log(`[handleStartTimer] Starting timer. Now (est): ${estimatedServerNow}, Duration: ${duration}, EndTime: ${endTimestamp}`);

    const updates = { segmentEndTime: endTimestamp };
    // Safety check: ensure prep timers are marked as paused in the same update
    if (isPrepARunning) { updates['prepTimers/sideA/running'] = false; }
    if (isPrepBRunning) { updates['prepTimers/sideB/running'] = false; }

    update(ref(db, `sessions/${sessionId}`), updates)
      .then(() => console.log("[handleStartTimer] Firebase updated with segmentEndTime:", endTimestamp))
      .catch(error => {
          console.error("[handleStartTimer] Failed to start timer:", error);
          alert(`Failed to start timer: ${error.message}`);
      });
  };

  // Updates Firebase for notes
  const handleFirebaseNoteUpdate = (segmentIndex, noteValue) => {
      if (role !== 'judge') { return; }
      // Simple update, debounce could be added for performance
      set(ref(db, `sessions/${sessionId}/notes/${segmentIndex}`), noteValue)
        .catch(error => { console.error("Failed to save note:", error); });
  };

  const handleNextSegment = () => {
    if (!session) return;
    const currentSegmentIndex = session.currentSegment ?? 0;
    const segments = session.type === 'LD' ? ldSegments : pfSegments;
    const nextSegmentIndex = currentSegmentIndex + 1;

    // Calculate isAnyPrepTimerRunning locally just before check
    const isPrepARunning = session.prepTimers?.sideA?.running ?? false;
    const isPrepBRunning = session.prepTimers?.sideB?.running ?? false;
    const anyPrepRunning = isPrepARunning || isPrepBRunning;

    if (anyPrepRunning) {
        console.warn("[handleNextSegment] Cannot advance segment: Prep timer running.");
        alert("Prep timer must be stopped to advance segment.");
        return;
    }

    if (nextSegmentIndex < segments.length) {
        const updates = { currentSegment: nextSegmentIndex, segmentEndTime: null };
        update(ref(db, `sessions/${sessionId}`), updates)
            .catch(error => { console.error("Failed to advance segment:", error); alert(`Failed to advance segment: ${error.message}`); });
    } else {
        // If it's the last segment, just clear the end time
        update(ref(db, `sessions/${sessionId}`), { segmentEndTime: null })
            .catch(error => console.error("Failed to clear end time:", error));
    }
  };

  const handleOpenConfig = () => { setIsConfigOpen(true); setConfigError(''); };
  const handleCloseConfig = () => { setIsConfigOpen(false); setConfigError(''); };
  const handleSaveConfig = async () => {
      setConfigError('');
      const newTimeMinutesParsed = parseInt(newPrepTimeMinutes, 10);
      if (isNaN(newTimeMinutesParsed) || newTimeMinutesParsed <= 0) { setConfigError('Please enter a valid positive number of minutes.'); return; }
      const newTimeMs = newTimeMinutesParsed * 60000;
      if (!session) { setConfigError('Session data not available.'); return; }
      const currentTotalPrepTime = session.totalPrepTime ?? (4 * 60000); // Use default if not set
      const updates = { totalPrepTime: newTimeMs };
      // Only reset remaining time if it hasn't been used yet (i.e., still equals the old total)
      if (session.prepTimers?.sideA?.remaining === currentTotalPrepTime) { updates['prepTimers/sideA/remaining'] = newTimeMs; }
      if (session.prepTimers?.sideB?.remaining === currentTotalPrepTime) { updates['prepTimers/sideB/remaining'] = newTimeMs; }
      try { await update(ref(db, `sessions/${sessionId}`), updates); handleCloseConfig(); }
      catch (error) { console.error("Failed to save config:", error); setConfigError(`Failed to save: ${error.message}`); }
  };

  // --- REMOVED: All Leave Button Handlers ---

  const handleDownloadNotes = () => {
      // Use localNotes for check, as it reflects the current UI state
      if (!localNotes || Object.keys(localNotes).length === 0) {
          alert("No notes available to download.");
          return;
      }
      if (role !== 'judge') {
          alert("Only the judge can download notes.");
          return;
      }
      const doc = new jsPDF();
      const segmentsDefinition = session.type === 'LD' ? ldSegments : pfSegments;
      const notesData = localNotes; // Use localNotes
      let yPos = 15;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 15;
      const maxWidth = doc.internal.pageSize.width - margin * 2;
      doc.setFontSize(16);
      doc.text(`Debate Notes - Session: ${sessionId}`, margin, yPos);
      yPos += 10;
      doc.setFontSize(12);
      segmentsDefinition.forEach((seg, index) => {
        const noteText = notesData[index] || '(No notes for this segment)';
        const segmentTitle = `${index + 1}. ${seg.name}`;
        if (yPos + 10 > pageHeight - margin) { doc.addPage(); yPos = margin; }
        doc.setFont(undefined, 'bold'); doc.text(segmentTitle, margin, yPos);
        doc.setFont(undefined, 'normal'); yPos += 7;
        const lines = doc.splitTextToSize(noteText, maxWidth);
        lines.forEach(line => {
            if (yPos + 5 > pageHeight - margin) { doc.addPage(); yPos = margin; }
            doc.text(line, margin, yPos); yPos += 5;
        });
        yPos += 5;
      });
      doc.save(`debate-notes-${sessionId}.pdf`);
  };
  // --- End Handler Functions ---


  // --- RENDER LOGIC ---

  // 1. Combined Loading State
  if ((session === undefined || !rejoinCheckComplete) && !errorState) {
    return (
<Container sx={{ py: { xs: 2, sm: 3 }, textAlign: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
          Debate Session Manager
        </Typography>
        <Typography variant="h6" component="p" color="text.secondary" sx={{ mb: 2 }}>
          Loading Session: {sessionId}
        </Typography>
        <CircularProgress sx={{ my: 3 }} />
        <Typography variant="body1" paragraph sx={{ maxWidth: '700px', mx: 'auto' }}>
          Attempting to load the debate session. This page facilitates live online debate rounds by providing synchronized timers, role management, participant status, and integrated judge notes.
        </Typography>

        <Paper variant="outlined" sx={{ p: 2, mt: 3, mb: 2, textAlign: 'left', maxWidth: '700px', mx: 'auto', bgcolor: 'background.default' }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>Supported Debate Formats:</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}><strong>Lincoln-Douglas (LD) Segments Overview:</strong></Typography>
          <List dense disablePadding sx={{ pl: 2, mb: 2 }}>
            {ldSegments.map((seg, i) => <ListItem key={`ld-static-${i}`} disableGutters sx={{ py: 0.25 }}><ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={`${i + 1}. ${seg.name} (${seg.duration / 60000} min)`} /></ListItem>)}
          </List>
          <Typography variant="body2" sx={{ mb: 1 }}><strong>Public Forum (PF) Segments Overview:</strong></Typography>
          <List dense disablePadding sx={{ pl: 2 }}>
            {pfSegments.map((seg, i) => <ListItem key={`pf-static-${i}`} disableGutters sx={{ py: 0.25 }}><ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={`${i + 1}. ${seg.name} (${seg.duration / 60000} min)`} /></ListItem>)}
          </List>
        </Paper>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Please wait while the specific session type and current state are loaded...
        </Typography>
      </Container>
    );
  }

  // 2. Error State or Session Not Found
  if (errorState || session === null) {
    return (
        <Container maxWidth="sm" sx={{ mt: 5, textAlign: 'center' }}>
            <Paper elevation={3} sx={{ p: 3 }}>
                <Typography variant="h5" color="error" gutterBottom>Session Ended or Error</Typography>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {errorState || `Session ${sessionId} is no longer available.`}
                </Alert>
                <Button variant="contained" onClick={() => navigate('/')}>
                    Go back to Home
                </Button>
            </Paper>
        </Container>
    );
  }

  // 3. Role Selection Screen
  // Show if session is loaded but role is not yet determined for this user
  if (!role) {
    // It's safe to access session.type here because session is guaranteed to be an object
    const options = session.type === 'LD'
      ? ['affirmative', 'negative', 'judge']
      : ['firstPro', 'secondPro', 'firstCon', 'secondCon', 'judge'];
    const getRoleIcon = (roleKey) => {
        if (roleKey.includes('judge')) return <GavelIcon />;
        if (roleKey.includes('affirmative') || roleKey.includes('Pro')) return <CheckCircleOutlineIcon />;
        if (roleKey.includes('negative') || roleKey.includes('Con')) return <CancelOutlinedIcon />;
        return <PersonIcon />;
    };
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh', p: 2 }}>
        <Container maxWidth="xs">
          <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 }, textAlign: 'center' }}>
             <Typography variant="h5" component="h1" gutterBottom>Join {session.type} Session</Typography>
             <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>Session ID: <strong>{sessionId}</strong></Typography>
             <Typography variant="h6" component="h2" sx={{ mb: 2 }}>Select Your Role:</Typography>
             <Stack spacing={2}>
              {options.map(opt => {
                  const isTaken = !!session.roles?.[opt];
                  const formattedName = formatRoleName(opt);
                  const icon = getRoleIcon(opt);
                  const description = (session.type === 'LD' ? roleDescriptions.LD[opt] : roleDescriptions.PF[opt]) || "Select this role to participate.";

                  return (
                    <Paper key={opt} variant="outlined" sx={{ p: 1.5, textAlign: 'left', opacity: isTaken ? 0.7 : 1 }}>
                      <Button
                        variant={isTaken ? "outlined" : "contained"}
                        color={isTaken ? "inherit" : "primary"}
                        onClick={() => joinRole(opt)}
                        disabled={loadingRole || isTaken}
                        size="large"
                        startIcon={
                          loadingRole && roleBeingJoined === opt ? <CircularProgress size={20} color="inherit" /> :
                          isTaken ? <LockIcon fontSize="small" /> : icon
                        }
                        sx={{ justifyContent: 'flex-start', textTransform: 'none', width: '100%', mb: 0.5 }}
                      >
                        {formattedName}
                      </Button>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 1 }}>
                        {description}
                      </Typography>
                    </Paper>
                  );
                })}
             </Stack>
          </Paper>
        </Container>
      </Box>
    );
  }

  // 4. Main Debate Session View
  // Render this only if loading/error/role selection checks have passed.
  const segments = session.type === 'LD' ? ldSegments : pfSegments;
  const current = session.currentSegment ?? 0;
  const isJudge = role === 'judge';
  const totalPrepTime = session.totalPrepTime ?? (4 * 60000); // Default prep time

  // Calculate timer states using estimated server time
  const estimatedServerTimeNow = Date.now() + serverTimeOffset;
  const isSegmentTimerRunning = session.segmentEndTime && estimatedServerTimeNow < session.segmentEndTime;
  const segmentTimerEnded = session.segmentEndTime && estimatedServerTimeNow >= session.segmentEndTime;
  const isPrepTimerARunning = session.prepTimers?.sideA?.running ?? false;
  const isPrepTimerBRunning = session.prepTimers?.sideB?.running ?? false;
  const isAnyPrepTimerRunning = isPrepTimerARunning || isPrepTimerBRunning;
  const isPrepTimeUsed = session.prepTimers?.sideA?.remaining < totalPrepTime || session.prepTimers?.sideB?.remaining < totalPrepTime;

  const allPossibleRoles = session.type === 'LD'
      ? ['affirmative', 'negative', 'judge']
      : ['firstPro', 'secondPro', 'firstCon', 'secondCon', 'judge'];

  return (
    <>
      {/* --- Main Layout Box (Flex Container for Content and Sidebar) --- */}

    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, p: { xs: 1, sm: 2 }, gap: { xs: 2, md: 3 } }}>
       {/* Main Content Area (Segments) */}
      <Box sx={{ flexGrow: 1 }}>
        {/* Header Paper */}
        <Paper elevation={1} sx={{ p: { xs: 1, sm: 2 }, mb: 2 }}>
             <Typography variant="h5" component="h1" gutterBottom sx={{ fontSize: { xs: '1.3rem', sm: '1.5rem' } }}>{session.type} Debate - Session: {sessionId}</Typography>
             <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle1" color="primary" sx={{ mb: 1 }}>Your Role: {formatRoleName(role)}</Typography>
                {/* --- REMOVED: Leave Session Buttons --- */}
                {/* Only show Download Notes for judge */}
                {isJudge && (<Button variant="outlined" color="secondary" size="small" startIcon={<DownloadIcon />} onClick={handleDownloadNotes} disabled={!localNotes || Object.keys(localNotes).length === 0} sx={{ mb: 1 }}>Download Notes</Button>)}
             </Box>
             <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="body2" sx={{ alignSelf: 'center', mr: 0.5, color: 'text.secondary' }}>Status:</Typography>
                {/* Status Chip Logic */}
                {allPossibleRoles.map(roleKey => {
                    const assignedUserId = session.roles?.[roleKey];
                    const participant = assignedUserId ? session.participants?.[assignedUserId] : null;
                    const isOnline = participant?.online === true; // Check the online status
                    const isTaken = !!assignedUserId;
                    let chipIcon = <RadioButtonUncheckedIcon fontSize="small" />;
                    let chipColor = "default";
                    let chipVariant = "outlined";
                    if (isTaken) { if (isOnline) { chipIcon = <CheckCircleIcon fontSize="small" />; chipColor = "success"; chipVariant = "filled"; } else { chipIcon = <RemoveCircleOutlineIcon fontSize="small" />; chipColor = "default"; chipVariant = "outlined"; } }
                    return (<Chip key={roleKey} icon={chipIcon} label={formatRoleName(roleKey)} color={chipColor} variant={chipVariant} size="small" />);
                })}
             </Box>
        </Paper>

        {/* Segments Mapping */}
        {segments.map((seg, idx) => (
          <Accordion key={idx} expanded={!isJudge ? idx === current : undefined} disabled={!isJudge && idx !== current} sx={{ my: 1, borderLeft: idx === current ? `4px solid ${theme.palette.primary.main}` : 'none', '&.Mui-expanded': { margin: '8px 0' }, '& .MuiAccordionSummary-root.Mui-disabled': { opacity: 0.7 }, '& .MuiAccordionDetails-root': { padding: theme.spacing(2) } }}>
            <AccordionSummary expandIcon={isJudge || idx === current ? <ExpandMoreIcon /> : null} aria-controls={`panel${idx}-content`} id={`panel${idx}-header`}>
              <Typography sx={{ fontWeight: idx === current ? 'bold' : 'normal', flexGrow: 1, fontSize: { xs: '0.9rem', sm: '1rem' } }}>{`${idx + 1}. ${seg.name} (${seg.duration / 60000} min)`}</Typography>
              {/* Display CountdownTimer if segmentEndTime exists and it's the current segment */}
              {session.segmentEndTime && idx === current && (<CountdownTimer endTime={session.segmentEndTime} variant="body2" sx={{ mr: 1, color: 'text.secondary' }} serverTimeOffset={serverTimeOffset} />)}
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                {/* Display Timer Controls/Progress only for the current segment */}
                {idx === current && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, width: '100%' }}>
                        {/* Display Progress Bar if segmentEndTime exists */}
                        {session.segmentEndTime && (<SegmentTimerWithProgress endTime={session.segmentEndTime} duration={seg.duration} sx={{ width: '100%', maxWidth: '400px', mb: 1 }} serverTimeOffset={serverTimeOffset} />)}
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                            {/* Display Start Button if allowed, no endTime, and timer hasn't ended */}
                            {seg.allowedRoles.includes(role) && !session.segmentEndTime && !segmentTimerEnded && (<Button variant="contained" color="success" onClick={() => handleStartTimer(seg.duration)} disabled={isAnyPrepTimerRunning} title={isAnyPrepTimerRunning ? "Pause prep timer to start segment" : ""}>Start Segment Timer</Button>)}
                            {/* Display Next Button if timer ended, allowed, and not last segment */}
                            {segmentTimerEnded && seg.allowedRoles.includes(role) && current < segments.length - 1 && (<Button variant="contained" color="primary" onClick={handleNextSegment} endIcon={<NavigateNextIcon />} disabled={isAnyPrepTimerRunning} title={isAnyPrepTimerRunning ? "Prep timer must be stopped" : ""}>Next Segment</Button>)}
                        </Box>
                         {/* Display End of Debate message */}
                         {segmentTimerEnded && current === segments.length - 1 && (<Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>End of Debate Round</Typography>)}
                    </Box>
                )}
                {/* Judge Notes TextField */}
                {isJudge && (
                  <TextField
                    label={`Judge Notes for: ${seg.name}`}
                    multiline
                    rows={4}
                    placeholder="Type your notes here..."
                    value={localNotes[idx] || ''} // Read from local state
                    onChange={(e) => {
                        const newNoteValue = e.target.value;
                        setLocalNotes(prev => ({ ...prev, [idx]: newNoteValue })); // Update local state immediately
                        handleFirebaseNoteUpdate(idx, newNoteValue); // Update Firebase
                    }}
                    variant="outlined"
                    fullWidth
                    sx={{ '& .MuiInputBase-inputMultiline': { resize: 'vertical', minHeight: '80px', } }}
                  />
                )}
                {/* Active Role Info */}
                {!isJudge && idx === current && (<Typography variant="body2" color="text.secondary">{seg.allowedRoles.map(formatRoleName).join(' / ')} active.</Typography>)}
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      {/* Prep Timers Area (Sidebar) */}
      <Paper elevation={3} sx={{ p: 2, width: { xs: '100%', md: '280px' }, height: 'fit-content', mt: { xs: 2, md: 0 } }}>
         <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 0, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>Prep Time</Typography>
            <IconButton aria-label="configure prep time" onClick={handleOpenConfig} disabled={isPrepTimeUsed} size="small" title={isPrepTimeUsed ? "Prep time cannot be changed after use" : "Configure Prep Time"}><SettingsIcon fontSize="inherit" /></IconButton>
         </Box>
         <Divider sx={{ mb: 2 }}/>
        <PrepTimer sessionId={sessionId} side="sideA" label={session.type === 'LD' ? 'Affirmative' : 'Pro'} roleMatch={['affirmative', 'firstPro', 'secondPro'].includes(role)} totalPrepTime={totalPrepTime} isSegmentTimerRunning={isSegmentTimerRunning} serverTimeOffset={serverTimeOffset} isOtherPrepTimerRunning={isPrepTimerBRunning} />
        <PrepTimer sessionId={sessionId} side="sideB" label={session.type === 'LD' ? 'Negative' : 'Con'} roleMatch={['negative', 'firstCon', 'secondCon'].includes(role)} totalPrepTime={totalPrepTime} isSegmentTimerRunning={isSegmentTimerRunning} serverTimeOffset={serverTimeOffset} isOtherPrepTimerRunning={isPrepTimerARunning} />
      </Paper>

      {/* Prep Time Configuration Dialog */}
      <Dialog open={isConfigOpen} onClose={handleCloseConfig} fullWidth maxWidth="xs">
           <DialogTitle>Configure Prep Time</DialogTitle>
           <DialogContent><TextField autoFocus margin="dense" id="prepTimeMinutes" label="Total Prep Time per Side (Minutes)" type="number" fullWidth variant="standard" value={newPrepTimeMinutes} onChange={(e) => setNewPrepTimeMinutes(e.target.value)} error={!!configError} helperText={configError || "Changes reset remaining time only if prep hasn't started."} inputProps={{ min: 1 }} /></DialogContent>
           <DialogActions><Button onClick={handleCloseConfig}>Cancel</Button><Button onClick={handleSaveConfig} variant="contained">Save</Button></DialogActions>
      </Dialog>

      {/* --- REMOVED: Judge Leave Confirmation Dialog --- */}

    </Box>
    {/* --- End Main Layout Box --- */}

    {/* AdSense Unit for Session Page - Placed at the bottom of the page content */}
    <Box sx={{ py: 2, display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
      <AdSenseUnit />
    </Box>

    {/* --- How to Use This Session Page Section --- */}
    <Container maxWidth="lg" sx={{ mt: 4, mb: 3 }}>
      <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 }, bgcolor: 'background.default' }}>
        <Typography variant="h6" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '1.1rem' }}>
          <InfoOutlinedIcon color="action" /> How to Use This Debate Session
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 36 }}><AccountCircleOutlinedIcon color="primary" fontSize="small" /></ListItemIcon>
            <ListItemText
              primary="Your Role"
              secondary="Your assigned role (e.g., Affirmative/Pro, Negative/Con, Judge) is displayed at the top. Ensure it is correct before proceeding."
            />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 36 }}><VpnKeyOutlinedIcon color="primary" fontSize="small" /></ListItemIcon>
            <ListItemText primary="Session ID" secondary="The unique Session ID is displayed at the top. Share this with others who need to join this specific round." />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 36 }}><VisibilityOutlinedIcon color="primary" fontSize="small" /></ListItemIcon>
            <ListItemText primary="Participant Status" secondary="See who has joined which role and their online status (green icon for online, grey outline for offline but role taken)." />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 36 }}><FormatListNumberedOutlinedIcon color="primary" fontSize="small" /></ListItemIcon>
            <ListItemText
              primary="Navigating Segments"
              secondary="The debate progresses through a list of timed segments (e.g., Constructive/Speaker, Rebuttal/Crossfire). The current active segment is highlighted. Only participants in allowed roles can start the timer for the current segment."
            />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 36 }}><SettingsIcon color="primary" fontSize="small" /></ListItemIcon>
            <ListItemText primary="Prep Time Configuration" secondary="The total prep time per side can be adjusted (by anyone, before prep is used) via the settings icon next to 'Prep Time'." />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 36 }}><AvTimerOutlinedIcon color="primary" fontSize="small" /></ListItemIcon>
            <ListItemText
              primary="Managing Timers"
              secondary="Segment Timer: Click 'Start Segment Timer' when it's your turn. Prep Timer: Each side has its own prep timer; click to start/pause. Prep time cannot run concurrently with a segment timer."
            />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 36 }}><SpeakerNotesOutlinedIcon color="primary" fontSize="small" /></ListItemIcon>
            <ListItemText
              primary="Judge's Notes (For Judges)"
              secondary="If you are the Judge, type notes in the text area for each segment. Notes save automatically (visible only to you) and can be downloaded as a PDF. Important: Notes are deleted when the judge leaves the session."
            />
          </ListItem>
          
        </List>
        <Typography variant="caption" component="p" sx={{ mt: 2, fontStyle: 'italic', color: 'text.secondary', textAlign: 'center' }}>
          This page is designed for live interaction. All timers, statuses, and notes are synchronized in real-time for all participants.
        </Typography>
      </Paper>
    </Container>
    {/* --- End: How to Use This Session Page Section --- */}

    </> // Close the root Fragment
  );
}
